import React, { useState, useEffect, useCallback } from 'react';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell,
} from 'recharts';
import api from '../../utils/api';
import Modal from '../../components/shared/Modal';
import { useConfirm } from '../../components/shared/Confirm';
import GastosFijos from './GastosFijos';
import CajaMenor from './CajaMenor';
import { useIsMobile } from '../../utils/useIsMobile';

const PESTANAS = [
  { id: 'resumen',      label: 'Resumen' },
  { id: 'ingresos',     label: 'Ingresos' },
  { id: 'gastos',       label: 'Gastos' },
  { id: 'gastos_fijos', label: 'Gastos fijos' },
  { id: 'caja_menor',   label: 'Caja menor' },
  { id: 'categorias',   label: 'Categorías' },
  { id: 'proveedores',  label: 'Proveedores' },
];

const METODOS = [
  'Transferencia', 'PSE', 'Tarjeta de crédito', 'Tarjeta débito',
  'Nequi', 'Daviplata', 'Efectivo', 'Otro',
];

// Cada cuanto se regenera un gasto fijo. '' = no se renueva.
const PERIODICIDADES = [
  { valor: '',           label: 'No se renueva (único)' },
  { valor: 'semanal',    label: 'Semanal' },
  { valor: 'mensual',    label: 'Mensual' },
  { valor: 'trimestral', label: 'Trimestral' },
  { valor: 'semestral',  label: 'Semestral' },
  { valor: 'anual',      label: 'Anual' },
];
const ETIQUETA_PERIODO = Object.fromEntries(
  PERIODICIDADES.filter(p => p.valor).map(p => [p.valor, p.label])
);

const VERDE = '#22c55e';
const ROJO  = '#ef4444';
const AZUL  = '#3b82f6';
const AMBAR = '#f59e0b';

const money = (n) => '$' + Number(n || 0).toLocaleString('es-CO', { maximumFractionDigits: 0 });
const hoy = () => new Date().toISOString().slice(0, 10);
const haceDias = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);
const enDias   = (n) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);

const fmtFecha = (f) => {
  if (!f) return '';
  const d = new Date(String(f).slice(0, 10) + 'T00:00:00');
  return isNaN(d) ? f : d.toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

/**
 * Que falta para que el aviso de vencimiento salga por WhatsApp.
 *
 * Hacen falta dos cosas y antes se decia solo que "no esta configurado", sin
 * indicar cual, que son sitios distintos del panel: el numero se vincula en
 * Integraciones y los celulares se cargan en la ficha de cada super admin.
 */
function faltaParaAvisar(wa) {
  const sinNumero = !wa?.instancia;
  const sinGente  = !wa?.destinatarios;

  if (sinNumero && sinGente) return 'Falta vincular el WhatsApp en Integraciones y cargar el celular de los super admins';
  if (sinNumero)             return 'Falta vincular el WhatsApp de la plataforma en Integraciones';
  return 'Ningún super admin tiene celular cargado: agrégalo en Usuarios';
}

export default function PlatformFinancePage({ defaultTab = 'resumen' }) {
  const [tab, setTab] = useState(defaultTab);
  const esMovil = useIsMobile(1100);

  const [resumen, setResumen]       = useState(null);
  const [alertas, setAlertas]       = useState([]);
  // { dias, whatsapp: { configurado, destinatarios } } — para explicar el aviso
  const [avisoConfig, setAvisoConfig] = useState(null);
  const [categorias, setCategorias] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [aviso, setAviso] = useState('');
  const [error, setError] = useState('');
  const [rango, setRango] = useState({ desde: haceDias(29), hasta: hoy() });

  const notificar = (msg, esError = false) => {
    if (esError) { setError(msg); setTimeout(() => setError(''), 4500); }
    else         { setAviso(msg); setTimeout(() => setAviso(''), 4500); }
  };

  const cargarCatalogos = useCallback(async () => {
    try {
      const [c, p] = await Promise.all([
        api.get('/finanzas/categorias'),
        api.get('/finanzas/proveedores'),
      ]);
      setCategorias(c.data || []);
      setProveedores(p.data || []);
    } catch { /* los desplegables quedan vacios; el error real se ve al guardar */ }
  }, []);

  const cargarResumen = useCallback(async () => {
    try {
      const [r, a] = await Promise.all([
        api.get('/finanzas/resumen', { params: rango }),
        api.get('/finanzas/alertas'),
      ]);
      setResumen(r.data);
      setAlertas(a.data.alertas || []);
      setAvisoConfig({ dias: a.data.dias_aviso, whatsapp: a.data.whatsapp });
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudieron cargar las finanzas');
    }
  }, [rango]);

  useEffect(() => { cargarCatalogos(); }, [cargarCatalogos]);
  useEffect(() => { cargarResumen(); }, [cargarResumen]);

  // Desde el aviso se cierra el gasto vencido sin tener que ir a buscarlo a la
  // pestaña. Al marcarlo pagado, el backend genera la siguiente ocurrencia si
  // el gasto es periodico.
  const [cerrando, setCerrando] = useState(null);
  const pagarYRenovar = async (alerta) => {
    setCerrando(alerta.id);
    try {
      const r = await api.patch(`/finanzas/movimientos/${alerta.id}/pagado`, { pagado: true });
      notificar(r.data?.renovados
        ? 'Pagado. Ya se creó el siguiente vencimiento.'
        : 'Marcado como pagado.');
      cargarResumen();
    } catch (err) {
      notificar(err.response?.data?.error || 'No se pudo actualizar el gasto', true);
    } finally { setCerrando(null); }
  };

  // Al guardar un movimiento hay que refrescar KPIs y alertas, no solo la lista.
  const trasGuardar = () => { cargarResumen(); };

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">Finanzas de la plataforma</h1>
          <p className="page-subtitle">Ingresos y gastos de la operación del SaaS</p>
        </div>
      </div>

      {aviso && <div className="alert alert-success" style={{ marginBottom: '1rem' }}>{aviso}</div>}
      {error && <div className="alert alert-danger"  style={{ marginBottom: '1rem' }}>{error}</div>}

      {/* Aviso de gastos fijos por vencer: visible en todas las pestañas */}
      {alertas.length > 0 && (
        <div className="card" style={{
          marginBottom: '1.25rem', padding: '1rem 1.25rem',
          borderLeft: `4px solid ${alertas.some(a => a.vencido) ? ROJO : AMBAR}`,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
            <span style={{ fontWeight: 700, fontSize: '0.92rem' }}>
              🔔 {alertas.length} gasto{alertas.length > 1 ? 's' : ''} fijo{alertas.length > 1 ? 's' : ''} por vencer
            </span>
            {avisoConfig && (
              <span style={{ fontSize: '0.76rem', color: 'var(--color-text-muted)' }}>
                {avisoConfig.whatsapp?.configurado
                  ? `Aviso por WhatsApp ${avisoConfig.dias} días antes · ${avisoConfig.whatsapp.destinatarios} super admin${avisoConfig.whatsapp.destinatarios === 1 ? '' : 's'}`
                  : `⚠ ${faltaParaAvisar(avisoConfig.whatsapp)}`}
              </span>
            )}
          </div>
          {alertas.map(a => (
            <div key={a.id} style={{
              display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap',
              alignItems: 'center',
              padding: '7px 0', fontSize: '0.85rem', borderTop: '1px solid var(--color-border)',
            }}>
              <button
                onClick={() => setTab('gastos_fijos')}
                title="Ver este gasto en la pestaña Gastos fijos"
                style={{
                  background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                  font: 'inherit', color: 'inherit', textAlign: 'left', minWidth: 0,
                }}
              >
                <strong style={{ textDecoration: 'underline' }}>{a.descripcion}</strong>
                {a.proveedor && <span style={{ color: 'var(--color-text-muted)' }}> · {a.proveedor}</span>}
              </button>
              <span style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ color: a.vencido ? ROJO : AMBAR, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {a.periodicidad && (
                    <span style={{ color: 'var(--color-text-muted)', fontWeight: 400 }}
                          title={`Se renueva ${ETIQUETA_PERIODO[a.periodicidad]?.toLowerCase() || ''}`}>↻ </span>
                  )}
                  {money(a.monto)} ·{' '}
                  {a.vencido ? `venció hace ${Math.abs(a.dias)} día${Math.abs(a.dias) === 1 ? '' : 's'}`
                    : a.dias === 0 ? 'vence hoy'
                    : a.dias === 1 ? 'vence mañana'
                    : `vence en ${a.dias} días`}
                </span>
                <button
                  className="btn btn-primary"
                  style={{ padding: '3px 11px', fontSize: '0.78rem', whiteSpace: 'nowrap' }}
                  disabled={cerrando === a.id}
                  title={a.periodicidad
                    ? 'Marca este vencimiento como pagado y crea el siguiente'
                    : 'Marca este gasto como pagado'}
                  onClick={() => pagarYRenovar(a)}
                >
                  {cerrando === a.id ? '...' : a.periodicidad ? '✓ Pagar y renovar' : '✓ Marcar pagado'}
                </button>
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Pestañas */}
      <div style={{
        display: 'flex', gap: 4, marginBottom: '1.5rem', flexWrap: 'wrap',
        borderBottom: '1px solid var(--color-border)',
      }}>
        {PESTANAS.map(p => (
          <button
            key={p.id}
            onClick={() => setTab(p.id)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '0.6rem 1rem', fontSize: '0.9rem',
              fontWeight: tab === p.id ? 700 : 500,
              color: tab === p.id ? 'var(--color-gold)' : 'var(--color-text-muted)',
              borderBottom: `2px solid ${tab === p.id ? 'var(--color-gold)' : 'transparent'}`,
              marginBottom: -1,
            }}
          >{p.label}</button>
        ))}
      </div>

      {tab === 'resumen' && (
        <Resumen resumen={resumen} rango={rango} setRango={setRango} esMovil={esMovil} />
      )}

      {tab === 'gastos_fijos' && (
        <GastosFijos
          categorias={categorias}
          proveedores={proveedores}
          esMovil={esMovil}
          notificar={notificar}
          onGuardado={trasGuardar}
          diasAviso={avisoConfig?.dias}
        />
      )}

      {tab === 'caja_menor' && (
        <CajaMenor
          categorias={categorias}
          esMovil={esMovil}
          notificar={notificar}
          onGuardado={trasGuardar}
        />
      )}

      {(tab === 'ingresos' || tab === 'gastos') && (
        <Movimientos
          tipo={tab === 'ingresos' ? 'ingreso' : 'gasto'}
          vista={tab === 'gastos' ? 'ejecutados' : null}
          categorias={categorias}
          proveedores={proveedores}
          esMovil={esMovil}
          notificar={notificar}
          onGuardado={trasGuardar}
          diasAviso={avisoConfig?.dias}
        />
      )}

      {tab === 'categorias' && (
        <Categorias categorias={categorias} recargar={cargarCatalogos} notificar={notificar} />
      )}

      {tab === 'proveedores' && (
        <Proveedores proveedores={proveedores} recargar={cargarCatalogos} notificar={notificar} />
      )}
    </div>
  );
}

// ── Resumen ─────────────────────────────────────────────────────────────────

function Kpi({ titulo, valor, variacion, color, icono, fondo, sufijo }) {
  const sube = variacion != null && variacion >= 0;
  return (
    <div className="card" style={{ padding: '1.1rem 1.25rem', minWidth: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', marginBottom: 4 }}>{titulo}</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, lineHeight: 1.15 }}>
            {valor}{sufijo || ''}
          </div>
          <div style={{ fontSize: '0.74rem', marginTop: 4, color: variacion == null ? 'var(--color-text-muted)' : (sube ? VERDE : ROJO) }}>
            {variacion == null ? 'Sin periodo previo' : `${sube ? '▲' : '▼'} ${Math.abs(variacion)}% vs período anterior`}
          </div>
        </div>
        <div style={{
          width: 40, height: 40, borderRadius: '50%', background: fondo, color,
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.05rem', flexShrink: 0,
        }}>{icono}</div>
      </div>
    </div>
  );
}

function Resumen({ resumen, rango, setRango, esMovil }) {
  if (!resumen) {
    return <div className="card" style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>Cargando...</div>;
  }

  const donut = [
    { name: 'Fijos',     value: resumen.gastosPorClase.fijo },
    { name: 'Variables', value: resumen.gastosPorClase.variable },
  ].filter(d => d.value > 0);

  const totalGastos = resumen.gastos || 0;
  const pct = (n) => (totalGastos > 0 ? Math.round((n / totalGastos) * 1000) / 10 : 0);

  return (
    <>
      <div style={{ display: 'flex', gap: 10, marginBottom: '1.25rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <input type="date" className="form-input" style={{ margin: 0, flex: '0 1 160px' }}
               value={rango.desde} onChange={e => setRango(r => ({ ...r, desde: e.target.value }))} />
        <input type="date" className="form-input" style={{ margin: 0, flex: '0 1 160px' }}
               value={rango.hasta} onChange={e => setRango(r => ({ ...r, hasta: e.target.value }))} />
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: esMovil ? 'repeat(auto-fit, minmax(160px, 1fr))' : 'repeat(4, 1fr)',
        gap: '1rem', marginBottom: '1.5rem',
      }}>
        <Kpi titulo="Ingresos totales" valor={money(resumen.ingresos)} variacion={resumen.variacion.ingresos}
             color={VERDE} fondo="rgba(34,197,94,0.12)" icono="📈" />
        <Kpi titulo="Gastos totales" valor={money(resumen.gastos)} variacion={resumen.variacion.gastos}
             color={ROJO} fondo="rgba(239,68,68,0.12)" icono="📉" />
        <Kpi titulo="Utilidad neta" valor={money(resumen.utilidad)} variacion={resumen.variacion.utilidad}
             color={AZUL} fondo="rgba(59,130,246,0.12)" icono="💼" />
        <Kpi titulo="Margen de utilidad" valor={resumen.margen} sufijo="%" variacion={resumen.variacion.margen}
             color="#8b5cf6" fondo="rgba(139,92,246,0.12)" icono="🥧" />
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: esMovil ? '1fr' : '1.6fr 1fr',
        gap: '1.25rem', marginBottom: '1.5rem',
      }}>
        <div className="card" style={{ padding: '1.25rem', minWidth: 0 }}>
          <h3 style={{ margin: '0 0 1rem', fontSize: '0.95rem' }}>Ingresos vs Gastos</h3>
          {resumen.serie.length === 0 ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
              Sin movimientos en el periodo
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={resumen.serie}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="fecha" tick={{ fontSize: 11 }}
                       tickFormatter={f => String(f).slice(8, 10)} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${Math.round(v / 1000)}k`} />
                <Tooltip formatter={v => money(v)} labelFormatter={fmtFecha} />
                <Legend />
                <Area type="monotone" dataKey="ingresos" name="Ingresos" stroke={VERDE} fill={VERDE} fillOpacity={0.18} />
                <Area type="monotone" dataKey="gastos"   name="Gastos"   stroke={ROJO}  fill={ROJO}  fillOpacity={0.18} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="card" style={{ padding: '1.25rem', minWidth: 0 }}>
          <h3 style={{ margin: '0 0 1rem', fontSize: '0.95rem' }}>Gastos por tipo</h3>
          {donut.length === 0 ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>Sin gastos</div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={donut} dataKey="value" nameKey="name" innerRadius={58} outerRadius={82} paddingAngle={2}>
                    {donut.map((d, i) => <Cell key={i} fill={i === 0 ? AZUL : AMBAR} />)}
                  </Pie>
                  <Tooltip formatter={v => money(v)} />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ marginTop: 10 }}>
                {[['Fijos', resumen.gastosPorClase.fijo, AZUL], ['Variables', resumen.gastosPorClase.variable, AMBAR]].map(([l, v, c]) => (
                  <div key={l} style={{ marginBottom: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', marginBottom: 3 }}>
                      <span><span style={{ color: c }}>■</span> {l}</span>
                      <span style={{ fontWeight: 600 }}>{money(v)} <span style={{ color: 'var(--color-text-muted)', fontWeight: 400 }}>({pct(v)}%)</span></span>
                    </div>
                    <div style={{ height: 4, borderRadius: 2, background: 'var(--color-border)' }}>
                      <div style={{ height: '100%', width: `${pct(v)}%`, background: c, borderRadius: 2 }} />
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {resumen.gastosPorCategoria.length > 0 && (
        <div className="card" style={{ padding: '1.25rem' }}>
          <h3 style={{ margin: '0 0 1rem', fontSize: '0.95rem' }}>Gastos por categoría</h3>
          {resumen.gastosPorCategoria.map(c => (
            <div key={c.categoria} style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: 3 }}>
                <span>{c.categoria}</span>
                <span style={{ fontWeight: 600 }}>{money(c.total)}</span>
              </div>
              <div style={{ height: 5, borderRadius: 3, background: 'var(--color-border)' }}>
                <div style={{ height: '100%', width: `${pct(c.total)}%`, background: ROJO, borderRadius: 3 }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ── Ingresos / Gastos ───────────────────────────────────────────────────────

const FORM_VACIO = {
  categoria_id: '', proveedor_id: '', descripcion: '', fecha: hoy(),
  monto: '', metodo_pago: '', notas: '', clase: 'variable', fecha_vencimiento: '',
  periodicidad: '', pagado: false,
};

function Movimientos({ tipo, vista, categorias, proveedores, esMovil, notificar, onGuardado, diasAviso = 5 }) {
  const esGasto = tipo === 'gasto';
  const esFijos = vista === 'fijos';

  // La pestaña Gastos es el registro de lo ya ejecutado: no se edita, no se
  // borra y no se renueva desde ahí. Un gasto fijo se corrige en su pestaña.
  const soloLectura = vista === 'ejecutados';

  // Nombre de lo que se registra aqui, para botones y mensajes.
  const queEs = esFijos ? 'gasto fijo' : esGasto ? 'gasto' : 'ingreso';
  const [items, setItems]   = useState([]);
  const [total, setTotal]   = useState(0);
  const [page, setPage]     = useState(1);
  const [loading, setLoading] = useState(true);
  const [form, setForm]     = useState(FORM_VACIO);
  const [editando, setEditando] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [abierto, setAbierto] = useState(false);
  const confirmar = useConfirm();

  const abrirNuevo = () => {
    setEditando(null);
    setForm({ ...FORM_VACIO, clase: esFijos ? 'fijo' : 'variable' });
    setAbierto(true);
  };
  const cerrarForm = () => { setAbierto(false); setEditando(null); setForm(FORM_VACIO); };
  // El rango llega hasta dentro de 60 días: un gasto fijo que vence la semana
  // que viene ya está registrado, y con la ventana cerrada en hoy no aparecía
  // por ningún lado aunque el aviso de arriba lo anunciara.
  const [filtros, setFiltros] = useState({ desde: haceDias(29), hasta: enDias(60), clase: '', buscar: '' });

  const LIMIT = 20;
  const cats = categorias.filter(c => c.tipo === tipo);

  const load = useCallback(async () => {
    setLoading(true);
    const params = { tipo, page, limit: LIMIT, ...filtros };
    if (vista) params.vista = vista;
    Object.keys(params).forEach(k => { if (params[k] === '') delete params[k]; });
    try {
      const r = await api.get('/finanzas/movimientos', { params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch { setItems([]); setTotal(0); }
    finally { setLoading(false); }
  }, [tipo, vista, page, filtros]);

  useEffect(() => { load(); }, [load]);
  // Al cambiar de pestaña (ingresos <-> gastos) se reinicia el formulario.
  useEffect(() => {
    // Al cambiar de pestaña se reinicia el formulario. Un gasto fijo nace ya
    // marcado como fijo: es lo unico que se registra en esa pestaña.
    setForm({ ...FORM_VACIO, clase: esFijos ? 'fijo' : 'variable' });
    setEditando(null); setAbierto(false); setPage(1);
  }, [tipo, vista]); // eslint-disable-line

  const guardar = async () => {
    if (!form.descripcion.trim()) return notificar('Escribe una descripción', true);
    if (!Number(form.monto))      return notificar('El monto debe ser mayor que cero', true);
    if (esGasto && form.clase === 'fijo' && !form.fecha_vencimiento) {
      return notificar('Un gasto fijo necesita fecha de vencimiento para poder avisarte', true);
    }
    setGuardando(true);
    try {
      const cuerpo = { ...form, tipo, monto: Number(form.monto) };
      if (editando) await api.put(`/finanzas/movimientos/${editando}`, cuerpo);
      else          await api.post('/finanzas/movimientos', cuerpo);
      notificar(editando ? 'Movimiento actualizado' : `${esGasto ? 'Gasto' : 'Ingreso'} registrado`);
      cerrarForm();
      load(); onGuardado();
    } catch (err) {
      notificar(err.response?.data?.error || 'No se pudo guardar', true);
    } finally { setGuardando(false); }
  };

  const editar = (m) => {
    setEditando(m.id);
    setForm({
      categoria_id: m.categoria_id || '', proveedor_id: m.proveedor_id || '',
      descripcion: m.descripcion, fecha: m.fecha, monto: String(m.monto),
      metodo_pago: m.metodo_pago || '', notas: m.notas || '',
      clase: m.clase || 'variable', fecha_vencimiento: m.fecha_vencimiento || '',
      periodicidad: m.periodicidad || '', pagado: !!m.pagado,
    });
    setAbierto(true);
  };

  const eliminar = async (m) => {
    if (!await confirmar({
      titulo: 'Eliminar movimiento',
      mensaje: `¿Eliminar "${m.descripcion}"?`,
      detalle: 'Esta acción no se puede deshacer.',
      aceptar: 'Eliminar',
      peligro: true,
    })) return;
    try {
      await api.delete(`/finanzas/movimientos/${m.id}`);
      notificar('Movimiento eliminado');
      load(); onGuardado();
    } catch (err) {
      notificar(err.response?.data?.error || 'No se pudo eliminar', true);
    }
  };

  // Marcar como pagado y renovar viven solo en la pestaña Gastos fijos
  // (GastosFijos.js). Desde este listado no se cambia el estado de nada.

  const paginas = Math.max(Math.ceil(total / LIMIT), 1);

  return (
    <div>
      {/* Listado. El formulario se abre en un modal, no en una columna al lado. */}
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.85rem' }}>
          <button className="btn btn-primary" onClick={abrirNuevo}>
            + Registrar {queEs}
          </button>
        </div>
        <div className="card" style={{ padding: '0.85rem 1rem', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input className="form-input" style={{ margin: 0, flex: '1 1 180px' }} placeholder="Buscar..."
                   value={filtros.buscar} onChange={e => { setPage(1); setFiltros(f => ({ ...f, buscar: e.target.value })); }} />
            <input type="date" className="form-input" style={{ margin: 0, flex: '0 1 150px' }}
                   value={filtros.desde} onChange={e => { setPage(1); setFiltros(f => ({ ...f, desde: e.target.value })); }} />
            <input type="date" className="form-input" style={{ margin: 0, flex: '0 1 150px' }}
                   value={filtros.hasta} onChange={e => { setPage(1); setFiltros(f => ({ ...f, hasta: e.target.value })); }} />
            {esGasto && (
              <select className="form-select" style={{ margin: 0, flex: '0 1 140px' }}
                      value={filtros.clase} onChange={e => { setPage(1); setFiltros(f => ({ ...f, clase: e.target.value })); }}>
                <option value="">Fijos y variables</option>
                <option value="fijo">Solo fijos</option>
                <option value="variable">Solo variables</option>
              </select>
            )}
          </div>
        </div>

        <div className="card" style={{ minWidth: 0 }}>
          {loading ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>Cargando...</div>
          ) : items.length === 0 ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
              No hay {esFijos ? 'gastos fijos' : esGasto ? 'gastos' : 'ingresos'} entre el {fmtFecha(filtros.desde)} y el {fmtFecha(filtros.hasta)}.
              <div style={{ fontSize: '0.8rem', marginTop: 4 }}>Amplía el rango de fechas si esperabas ver alguno.</div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="table" style={{ width: '100%', minWidth: 700 }}>
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Descripción</th>
                    <th>Categoría</th>
                    {esGasto && <th>Tipo</th>}
                    <th style={{ textAlign: 'right' }}>Monto</th>
                    {!soloLectura && <th className="col-acciones">Acciones</th>}
                  </tr>
                </thead>
                <tbody>
                  {items.map(m => (
                    <tr key={m.id}>
                      <td style={{ whiteSpace: 'nowrap', fontSize: '0.82rem' }}>{fmtFecha(m.fecha)}</td>
                      <td style={{ fontSize: '0.85rem' }}>
                        {m.descripcion}
                        {m.proveedor && <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{m.proveedor}</div>}
                        {m.clase === 'fijo' && m.fecha_vencimiento && (
                          <div style={{ fontSize: '0.72rem', color: m.pagado ? VERDE : AMBAR }}>
                            {m.pagado ? '✓ Pagado' : `Vence ${fmtFecha(m.fecha_vencimiento)}`}
                            {m.periodicidad && (
                              <span style={{ color: 'var(--color-text-muted)' }}>
                                {' · ↻ '}{ETIQUETA_PERIODO[m.periodicidad] || m.periodicidad}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)' }}>{m.categoria || '—'}</td>
                      {esGasto && (
                        <td>
                          <span style={{
                            padding: '2px 8px', borderRadius: 999, fontSize: '0.7rem', fontWeight: 600,
                            background: m.clase === 'fijo' ? 'rgba(59,130,246,0.12)' : 'rgba(245,158,11,0.12)',
                            color: m.clase === 'fijo' ? AZUL : AMBAR,
                          }}>{m.clase === 'fijo' ? 'Fijo' : 'Variable'}</span>
                        </td>
                      )}
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 600, color: esGasto ? ROJO : VERDE }}>
                        {money(m.monto)}
                      </td>
                      {/* Gastos es el libro de lo ya ejecutado: solo se
                          registra. Los gastos fijos se gestionan en su
                          propia pestaña. */}
                      {!soloLectura && (
                        <td className="col-acciones">
                          <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                            <button className="btn btn-secondary btn-icon" title="Editar"
                                    onClick={() => editar(m)}>✎</button>
                            <button className="btn btn-ghost btn-icon" title="Eliminar"
                                    onClick={() => eliminar(m)}>🗑</button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
            padding: '0.85rem 1.25rem', borderTop: '1px solid var(--color-border)', flexWrap: 'wrap',
          }}>
            <span style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)' }}>{total} registros</span>
            {paginas > 1 && (
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="btn btn-ghost" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>←</button>
                <span style={{ fontSize: '0.82rem', alignSelf: 'center' }}>{page} / {paginas}</span>
                <button className="btn btn-ghost" disabled={page >= paginas} onClick={() => setPage(p => p + 1)}>→</button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Formulario */}
      {abierto && (
        <Modal
          titulo={`${editando ? 'Editar' : 'Registrar'} ${queEs}`}
          ancho={520}
          onCerrar={cerrarForm}
          pie={<>
            <button className="btn btn-ghost" onClick={cerrarForm} disabled={guardando}>Cancelar</button>
            <button className="btn btn-primary" disabled={guardando} onClick={guardar}>
              {guardando ? 'Guardando...' : editando ? 'Guardar cambios' : `Guardar ${esGasto ? 'gasto' : 'ingreso'}`}
            </button>
          </>}
        >
        <div>
          {false && (
            <div className="form-group">
              <label className="form-label">Tipo de gasto</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {[['variable', 'Variable'], ['fijo', 'Fijo']].map(([v, l]) => (
                  <button key={v} type="button"
                          className={`btn ${form.clase === v ? 'btn-primary' : 'btn-ghost'}`}
                          style={{ flex: 1, fontSize: '0.85rem' }}
                          onClick={() => setForm(f => ({ ...f, clase: v }))}>{l}</button>
                ))}
              </div>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Categoría</label>
            <select className="form-select" value={form.categoria_id}
                    onChange={e => setForm(f => ({ ...f, categoria_id: e.target.value }))}>
              <option value="">Selecciona categoría</option>
              {cats.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
          </div>

          {esGasto && (
            <div className="form-group">
              <label className="form-label">Proveedor</label>
              <select className="form-select" value={form.proveedor_id}
                      onChange={e => setForm(f => ({ ...f, proveedor_id: e.target.value }))}>
                <option value="">Sin proveedor</option>
                {proveedores.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
              </select>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Descripción</label>
            <input className="form-input" placeholder={esGasto ? 'Ej. Arriendo oficina' : 'Ej. Venta de productos'}
                   value={form.descripcion} onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))} />
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Fecha</label>
              <input type="date" className="form-input" value={form.fecha}
                     onChange={e => setForm(f => ({ ...f, fecha: e.target.value }))} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Monto</label>
              <input type="number" min="0" step="1" className="form-input" placeholder="0"
                     value={form.monto} onChange={e => setForm(f => ({ ...f, monto: e.target.value }))} />
            </div>
          </div>

          {esGasto && form.clase === 'fijo' && (
            <>
              <div className="form-group">
                <label className="form-label">
                  {form.periodicidad ? 'Primer vencimiento' : 'Fecha de vencimiento'}
                </label>
                <input type="date" className="form-input" value={form.fecha_vencimiento}
                       onChange={e => setForm(f => ({ ...f, fecha_vencimiento: e.target.value }))} />
                <p style={{ margin: '5px 2px 0', fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                  Recibirás un aviso {diasAviso} días antes de esta fecha.
                </p>
              </div>

              <div className="form-group">
                <label className="form-label">Se renueva</label>
                <select className="form-select" value={form.periodicidad}
                        onChange={e => setForm(f => ({ ...f, periodicidad: e.target.value }))}>
                  {PERIODICIDADES.map(p => <option key={p.valor} value={p.valor}>{p.label}</option>)}
                </select>
                {form.periodicidad && (
                  <p style={{ margin: '5px 2px 0', fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                    Al vencer o al marcarlo pagado, se creará solo el siguiente
                    con la fecha corrida {ETIQUETA_PERIODO[form.periodicidad].toLowerCase()}.
                  </p>
                )}
              </div>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.pagado}
                         onChange={e => setForm(f => ({ ...f, pagado: e.target.checked }))} />
                  Ya está pagado
                </label>
              </div>
            </>
          )}

          <div className="form-group">
            <label className="form-label">Método de pago</label>
            <select className="form-select" value={form.metodo_pago}
                    onChange={e => setForm(f => ({ ...f, metodo_pago: e.target.value }))}>
              <option value="">Selecciona método</option>
              {METODOS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Notas (opcional)</label>
            <textarea className="form-input" rows={2} value={form.notas}
                      onChange={e => setForm(f => ({ ...f, notas: e.target.value }))} />
          </div>

        </div>
        </Modal>
      )}
    </div>
  );
}

// ── Categorías ──────────────────────────────────────────────────────────────

function Categorias({ categorias, recargar, notificar }) {
  const [nombre, setNombre] = useState('');
  const [tipo, setTipo] = useState('gasto');
  const [guardando, setGuardando] = useState(false);
  const confirmar = useConfirm();

  const crear = async () => {
    if (!nombre.trim()) return notificar('Escribe el nombre de la categoría', true);
    setGuardando(true);
    try {
      await api.post('/finanzas/categorias', { nombre, tipo });
      setNombre(''); notificar('Categoría creada'); recargar();
    } catch (err) {
      notificar(err.response?.data?.error || 'No se pudo crear', true);
    } finally { setGuardando(false); }
  };

  const desactivar = async (c) => {
    if (!await confirmar({
      titulo: 'Desactivar categoría',
      mensaje: `¿Desactivar "${c.nombre}"?`,
      detalle: 'Los movimientos que ya la usan la conservan.',
      aceptar: 'Desactivar',
      peligro: true,
    })) return;
    try {
      await api.delete(`/finanzas/categorias/${c.id}`);
      notificar('Categoría desactivada'); recargar();
    } catch (err) {
      notificar(err.response?.data?.error || 'No se pudo desactivar', true);
    }
  };

  const lista = (t) => categorias.filter(c => c.tipo === t);

  return (
    <>
      <div className="card" style={{ padding: '1.25rem', marginBottom: '1.25rem' }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 220px' }}>
            <label className="form-label">Nueva categoría</label>
            <input className="form-input" style={{ margin: 0 }} value={nombre} placeholder="Ej. Servidores"
                   onChange={e => setNombre(e.target.value)} />
          </div>
          <div style={{ flex: '0 1 160px' }}>
            <label className="form-label">Tipo</label>
            <select className="form-select" style={{ margin: 0 }} value={tipo} onChange={e => setTipo(e.target.value)}>
              <option value="gasto">Gasto</option>
              <option value="ingreso">Ingreso</option>
            </select>
          </div>
          <button className="btn btn-primary" disabled={guardando} onClick={crear}>+ Agregar</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem' }}>
        {[['ingreso', 'Categorías de ingreso'], ['gasto', 'Categorías de gasto']].map(([t, titulo]) => (
          <div key={t} className="card">
            <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid var(--color-border)' }}>
              <h3 style={{ margin: 0, fontSize: '0.95rem' }}>{titulo}</h3>
            </div>
            {lista(t).length === 0 ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>Sin categorías</div>
            ) : lista(t).map(c => (
              <div key={c.id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
                padding: '0.7rem 1.25rem', borderBottom: '1px solid var(--color-border)', fontSize: '0.87rem',
              }}>
                <span>{c.nombre}</span>
                <button className="btn btn-ghost" style={{ padding: '2px 8px', fontSize: '0.78rem' }}
                        onClick={() => desactivar(c)}>🗑</button>
              </div>
            ))}
          </div>
        ))}
      </div>
    </>
  );
}

// ── Proveedores ─────────────────────────────────────────────────────────────

const PROV_VACIO = { nombre: '', nit: '', contacto: '', telefono: '', email: '', notas: '' };

function Proveedores({ proveedores, recargar, notificar }) {
  const [form, setForm] = useState(PROV_VACIO);
  const [editando, setEditando] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [abierto, setAbierto] = useState(false);
  const confirmar = useConfirm();

  const abrirNuevo = () => { setEditando(null); setForm(PROV_VACIO); setAbierto(true); };
  const cerrarForm = () => { setAbierto(false); setEditando(null); setForm(PROV_VACIO); };

  const guardar = async () => {
    if (!form.nombre.trim()) return notificar('Escribe el nombre del proveedor', true);
    setGuardando(true);
    try {
      if (editando) await api.put(`/finanzas/proveedores/${editando}`, form);
      else          await api.post('/finanzas/proveedores', form);
      notificar(editando ? 'Proveedor actualizado' : 'Proveedor creado');
      cerrarForm(); recargar();
    } catch (err) {
      notificar(err.response?.data?.error || 'No se pudo guardar', true);
    } finally { setGuardando(false); }
  };

  const desactivar = async (p) => {
    if (!await confirmar({
      titulo: 'Desactivar proveedor',
      mensaje: `¿Desactivar "${p.nombre}"?`,
      detalle: 'Los movimientos que ya lo tienen asignado lo conservan.',
      aceptar: 'Desactivar',
      peligro: true,
    })) return;
    try {
      await api.delete(`/finanzas/proveedores/${p.id}`);
      notificar('Proveedor desactivado'); recargar();
    } catch (err) {
      notificar(err.response?.data?.error || 'No se pudo desactivar', true);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.85rem' }}>
        <button className="btn btn-primary" onClick={abrirNuevo}>+ Nuevo proveedor</button>
      </div>

      <div className="card" style={{ minWidth: 0 }}>
        {proveedores.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
            Aún no hay proveedores
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table" style={{ width: '100%', minWidth: 560 }}>
              <thead>
                <tr>
                  <th>Proveedor</th><th>NIT</th><th>Contacto</th>
                  <th style={{ textAlign: 'right' }}>Gastado</th>
                  <th style={{ textAlign: 'right' }}></th>
                </tr>
              </thead>
              <tbody>
                {proveedores.map(p => (
                  <tr key={p.id}>
                    <td style={{ fontSize: '0.87rem', fontWeight: 500 }}>{p.nombre}</td>
                    <td style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)' }}>{p.nit || '—'}</td>
                    <td style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)' }}>
                      {p.contacto || '—'}
                      {p.telefono && <div>{p.telefono}</div>}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap' }}>{money(p.total)}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button className="btn btn-ghost" style={{ padding: '2px 7px', fontSize: '0.78rem' }}
                              onClick={() => { setEditando(p.id); setForm({ ...PROV_VACIO, ...p }); setAbierto(true); }}>✎</button>
                      <button className="btn btn-ghost" style={{ padding: '2px 7px', fontSize: '0.78rem', marginLeft: 3 }}
                              onClick={() => desactivar(p)}>🗑</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {abierto && (
        <Modal
          titulo={`${editando ? 'Editar' : 'Nuevo'} proveedor`}
          ancho={480}
          onCerrar={cerrarForm}
          pie={<>
            <button className="btn btn-ghost" onClick={cerrarForm} disabled={guardando}>Cancelar</button>
            <button className="btn btn-primary" disabled={guardando} onClick={guardar}>
              {guardando ? 'Guardando...' : editando ? 'Guardar cambios' : 'Crear proveedor'}
            </button>
          </>}
        >
        <div>
          {[
            ['nombre', 'Nombre', 'Ej. Hostinger'],
            ['nit', 'NIT', ''],
            ['contacto', 'Persona de contacto', ''],
            ['telefono', 'Teléfono', ''],
            ['email', 'Correo', ''],
          ].map(([campo, etiqueta, ph]) => (
            <div className="form-group" key={campo}>
              <label className="form-label">{etiqueta}</label>
              <input className="form-input" placeholder={ph} value={form[campo] || ''}
                     onChange={e => setForm(f => ({ ...f, [campo]: e.target.value }))} />
            </div>
          ))}
          <div className="form-group">
            <label className="form-label">Notas</label>
            <textarea className="form-input" rows={2} value={form.notas || ''}
                      onChange={e => setForm(f => ({ ...f, notas: e.target.value }))} />
          </div>
        </div>
        </Modal>
      )}
    </div>
  );
}
